/**
 * DXF ingest banner — the "is it actually running?" surface.
 *
 * The container reports NOTHING between dispatch and done.json, so there is no
 * honest percentage to draw for the phase that consumes the time. What there
 * is, is a dispatch timestamp — and a clock ticking off it is motion the user
 * can trust, because every second of it is measured rather than modelled.
 *
 * It lives here, over the map, rather than in the Plans ticket, because that
 * was the actual defect behind "I have no way to tell if the process is
 * running": all ingest feedback rendered inside PlansTicket and vanished the
 * moment any other ticket was opened, while the poller kept running page-wide.
 * A >500MB sheet then looked identical to nothing happening at all.
 *
 * Read-only share viewers see none of it: ShareViewerPage mounts EditorPage
 * wholesale, so anything added to `.map-area` is on the client-facing surface
 * unless it is gated.
 */

import { useEffect, useState } from 'react';
import { useProjectStore } from '../../stores';
import { useIsReadOnlyViewer } from '../../hooks/useShareViewer';
import { recheckErroredIngests, formatElapsed } from '../../hooks/useDxfIngestPoller';
import './DxfIngestBanner.css';

export default function DxfIngestBanner() {
  const isReadOnly = useIsReadOnlyViewer();
  const overlays = useProjectStore((s) => s.data.overlays);
  const [now, setNow] = useState(() => Date.now());
  const [checking, setChecking] = useState(false);

  const running = overlays.filter((o) => o.dxf?.status === 'ingesting');
  const stalled = overlays.filter((o) => o.dxf?.status === 'error' && o.dxf.stalled);
  const show = !isReadOnly && (running.length > 0 || stalled.length > 0);

  // One interval for the whole banner, and only while something is running —
  // a stalled row has no clock to advance.
  useEffect(() => {
    if (!show || running.length === 0) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [show, running.length]);

  if (!show) return null;

  const handleCheckNow = async () => {
    if (checking) return;
    setChecking(true);
    try {
      await recheckErroredIngests({ force: true });
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="dxf-ingest-banner" data-testid="dxf-ingest-banner">
      {running.map((o) => {
        const started = o.dxf?.startedAt ? Date.parse(o.dxf.startedAt) : NaN;
        return (
          <div className="dxf-ingest-banner__row" key={o.id}>
            <span className="dxf-ingest-banner__spinner" aria-hidden="true" />
            <span className="dxf-ingest-banner__label">
              Reading DXF — {o.name}
            </span>
            {/* No elapsed for jobs dispatched before startedAt existed: an
                absent number beats a wrong one. */}
            {!Number.isNaN(started) && (
              <span className="dxf-ingest-banner__clock">{formatElapsed(now - started)}</span>
            )}
            <span className="dxf-ingest-banner__hint">
              large sheets take several minutes — you can keep working
            </span>
          </div>
        );
      })}

      {stalled.map((o) => (
        <div className="dxf-ingest-banner__row dxf-ingest-banner__row--stalled" key={o.id}>
          <span className="dxf-ingest-banner__label">
            Stopped waiting on the DXF for {o.name} — it may still be running on the server.
          </span>
          <button
            type="button"
            className="dxf-ingest-banner__action"
            onClick={handleCheckNow}
            disabled={checking}
            data-testid="dxf-ingest-check-now"
          >
            {checking ? 'Checking…' : 'Check now'}
          </button>
        </div>
      ))}
    </div>
  );
}
